import { Injectable } from '@angular/core';
import { cloneDeep } from 'lodash';
import { concat, Observable, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { ClientInterface, ContractInterface, FullClientInterface } from '../data';
import { ClientProvider } from '../providers/client.provider';
import { ContractProvider } from '../providers/contract.provider';
import { Transaction } from '../../domain/transaction/transaction';

@Injectable()
export class ContractService {
    constructor(private clientProvider: ClientProvider, private contractProvider: ContractProvider) {}

    getAppropriatenessClient(): Observable<Transaction<ClientInterface>> {
        return this.contractProvider.current().pipe(
            map((transaction) => {
                if (transaction.isSuccess) {
                    if (transaction.data.appropriatenessClientDocId) {
                        let client: ClientInterface;

                        transaction.data.holders.map((holder) => {
                            if (holder.docId === transaction.data.appropriatenessClientDocId) {
                                client = holder;
                            }
                        });

                        if (!client) {
                            transaction.data.legalGuardians.map((legalGuardian) => {
                                if (legalGuardian.docId === transaction.data.appropriatenessClientDocId) {
                                    client = legalGuardian;
                                }
                            });
                        }

                        if (client) {
                            return Transaction.success(cloneDeep(client));
                        }
                    }

                    return Transaction.error<ClientInterface>(transaction.error);
                } else {
                    return Transaction.error<ClientInterface>(transaction.error);
                }
            })
        );
    }

    getParticipants(isFull = false): Observable<Transaction<ClientInterface[]>> {
        return this.contractProvider.current().pipe(
            switchMap((transaction) =>
                transaction.map(
                    (contract) => {
                        const participants: ClientInterface[] = [];

                        contract.holders.map((holder) => {
                            participants.push(holder);
                        });

                        contract.authorized.map((authorized) => {
                            participants.push(authorized);
                        });

                        contract.legalGuardians.map((legalGuardian) => {
                            if (!participants.find((it) => it.id.value === legalGuardian.id.value)) {
                                participants.push(legalGuardian);
                            }
                        });

                        if (isFull) {
                            return this.getFullParticipants(participants, contract);
                        }

                        return of(Transaction.success(participants));
                    },
                    (throwable) => of(Transaction.error(throwable.message))
                )
            )
        );
    }

    private getFullParticipants(
        participants: ClientInterface[],
        contract: ContractInterface
    ): Observable<Transaction<FullClientInterface[]>> {
        return of(true).pipe(
            switchMap(() => {
                const observables: Observable<Transaction<FullClientInterface>>[] = [];
                const fullParticipants: FullClientInterface[] = [];

                participants.forEach((participant) => {
                    observables.push(
                        this.clientProvider.find(participant.encryptedCode, contract).pipe(
                            tap((transaction) => {
                                if (transaction.isSuccess) {
                                    fullParticipants.push(transaction.data);
                                }
                            })
                        )
                    );
                });

                if (observables.length > 0) {
                    return concat(...observables).pipe(map(() => Transaction.success(fullParticipants)));
                }

                return of(Transaction.success(fullParticipants));
            })
        );
    }
}
